import {useMemo, useState, useEffect} from 'react'
import Image from 'next/image'
import {useSelector, useDispatch} from 'react-redux'
import {get} from '@cullylarson/f'
import {preventDefault} from '@@client/lib/events'
import {getEnding} from '@@client/lib/numbers'
import {useTokenCache, useScript} from '@@client/lib/hooks'
import {
    SHIPPING_METHOD_HOME,
    PAYMENT_METHOD_BANDR_ACCOUNT,
    PAYMENT_METHOD_PAYPAL,
    PAYMENT_METHOD_CREDIT_CARD,
    PAYMENT_METHOD_AFFIRM,
    getOrderTotals,
} from './checkout-utils'
import {bigBlueStyle} from '@@client/checkout/checkout-styles'
import {
    setPaymentMethod,
    setCheckoutField,
    fillBillingAddressFromShippingAddress,
    payWithBandrAccount,
    payWithCreditCard,
    payWithPaypal,
    payWithAffirm,
} from './actions'
import loadCreditCardKey from './loadCreditCardKey'
import Loading from '@@client/components/Loading'
import Messages from '@@client/components/Messages'
import PlaceholderDots from '@@client/components/PlaceholderDots'
import FormText from '@@client/components/FormText'
import FormTextArea from '@@client/components/FormTextArea'
import FormNumber from '@@client/components/FormNumber'
import FormPrice from '@@client/components/FormPrice'
import FormPhone from '@@client/components/FormPhone'
import FormEmail from '@@client/components/FormEmail'
import FormCheckboxList from '@@client/components/FormCheckboxList'
import FormUSStateSelect from '@@client/components/FormUSStateSelect'
import FormSelect from '@@client/components/FormSelect'
import FormCybersourceField, {flexFormStyles} from '@@client/components/FormCybersourceField'
import StepSummaryItem from './StepSummaryItem'
import NextStepButton from './NextStepButton'

const BandrAccountPaymentForm = ({
    onSubmit,
    errors,
    paramErrors,
    disabled = false,
}) => {
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const idPrefix = 'checkout-payment-bandr-account'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    const handleSubmit = preventDefault(() => {
        dispatch(payWithBandrAccount(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    })

    return (
        <>
            <Messages error={errors} />

            <h4>B&R Account Information:</h4>

            <form onSubmit={handleSubmit}>
                <div className='items'>
                    <FormText
                        idPrefix={idPrefix}
                        name='name'
                        title='Name / Company'
                        value={checkoutInfo.bandrAccountName}
                        onChange={handleParam('bandrAccountName')}
                        errors={get('bandrAccountName', [], paramErrors)}
                        required
                    />

                    <FormNumber
                        idPrefix={idPrefix}
                        name='accountNumber'
                        title='Account Number'
                        value={checkoutInfo.bandrAccountNumber}
                        onChange={handleParam('bandrAccountNumber')}
                        errors={get('bandrAccountNumber', [], paramErrors)}
                        required
                    />

                    <FormText
                        idPrefix={idPrefix}
                        name='poNumber'
                        title='P.O. Number'
                        value={checkoutInfo.bandrAccountPoNumber}
                        onChange={handleParam('bandrAccountPoNumber')}
                        errors={get('bandrAccountPoNumber', [], paramErrors)}
                        required
                    />
                </div>

                <CommentField disabled={disabled} />

                <NextStepButton disabled={disabled}>Next: Review Order</NextStepButton>
            </form>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .items {
                        max-width: 400px;
                    }

                    .field-sets {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        grid-gap: 30px;
                    }

                    .state-zip {
                        display: grid;
                        grid-template-columns: 2fr 1fr;
                        grid-gap: 10px;
                    }

                    .expiration-security {
                        display: grid;
                        grid-template-columns: 0.8fr 1fr 0.7fr;
                        grid-gap: 10px;
                    }

                    .hack-label {
                        margin-bottom: 0;
                    }

                    .use-shipping {
                        margin-top: 20px;
                        margin-bottom: 10px;
                    }
                `}
            </style>
        </>
    )
}

function getIsFlexLoaded() {
    return Boolean(window?.Flex)
}

// Since we're including the client-side library returned from the CybersourceAPI, after we get a response back, we need to wait for the library to load before we can use it. This waits for it.
function useWaitForFlexToLoad(creditCardClientLibrary, maxTimeoutMs = 5000) {
    const [startCheckingAt, setStartCheckingAt] = useState(Date.now())
    const [creditCardClientLibraryKnownValue, setCreditCardClientLibraryKnownValue] = useState(creditCardClientLibrary)
    const [isFlexLoaded, setIsFlexLoaded] = useState(getIsFlexLoaded())

    useEffect(() => {
        // we need to recheck whether flex is loaded after creditCardClientLibrary changes
        if(creditCardClientLibraryKnownValue !== creditCardClientLibrary) {
            setCreditCardClientLibraryKnownValue(creditCardClientLibrary)
            setIsFlexLoaded(getIsFlexLoaded())
            setStartCheckingAt(Date.now())
        }
    }, [creditCardClientLibrary])

    useEffect(() => {
        if(isFlexLoaded || Date.now() > startCheckingAt + maxTimeoutMs) {
            return
        }

        const interval = setInterval(() => {
            if(getIsFlexLoaded()) {
                setIsFlexLoaded(true)
                clearInterval(interval)
                return
            }

            if(Date.now() > startCheckingAt + maxTimeoutMs) {
                clearInterval(interval)
                return
            }
        }, 100)

        return () => clearInterval(interval)
    }, [creditCardClientLibrary])

    return {isFlexLoaded}
}

const CreditCardPaymentForm = ({
    onSubmit,
    errors,
    paramErrors,
    disabled = false,
}) => {
    loadCreditCardKey()

    const [microform, setMicroform] = useState(null)
    const [completeFailureErrors, setCompleteFailureErrors] = useState([])
    const [cybersourceErrors, setCybersourceErrors] = useState([])
    const [cardNumberParamErrors, setCardNumberParamErrors] = useState([])
    const [securityCodeParamErrors, setSecurityCodeParamErrors] = useState([])
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const config = useSelector(state => state.config)
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info
    const {isFlexLoaded} = useWaitForFlexToLoad(checkoutInfo.creditCardClientLibrary)

    const idPrefix = 'checkout-payment-credit-card'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    const handleSubmit = preventDefault(() => {
        if(!microform) return

        microform.createToken({}, (err, token) => {
            setCybersourceErrors([])
            setCardNumberParamErrors([])
            setSecurityCodeParamErrors([])

            if(err) {
                if(!err?.reason || err.reason !== 'CREATE_TOKEN_VALIDATION_FIELDS' || !err?.details || !err.details.length) {
                    setCybersourceErrors(['Something went wrong and your credit card information could not be verified. Please try again. If you continue to have this issue, try refreshing the page and try again.'])
                }
                else {
                    setCybersourceErrors(['Something is wrong with your credit card number or security code. Please see error messages below.'])

                    err.details.forEach(x => {
                        if(x.location === 'number') {
                            const message = x.message === 'Validation error'
                                ? 'The credit card number you have entered is not valid.'
                                : x.message

                            setCardNumberParamErrors([message])
                        }
                        else if(x.location === 'securityCode') {
                            const message = x.message === 'Validation error'
                                ? 'The security code you have entered is not valid.'
                                : x.message

                            setSecurityCodeParamErrors([message])
                        }
                    })
                }
            }
            else {
                dispatch(payWithCreditCard(tokenCache, token))
                    .then(success => {
                        if(success) {
                            onSubmit()
                        }
                    })
            }
        })
    })

    const handleCreateCybersourceFieldError = () => {
        setCompleteFailureErrors(['Something went wrong and the credit card payment session could not be initiated. Please refresh to try again.'])
    }

    const expirationMonthOptions = useMemo(() => {
        const padMonth = x => {
            x = x.toString()

            return x.length === 1
                ? '0' + x
                : x
        }

        const options = []
        for(let i = 1; i <= 12; i++) {
            const valueStr = padMonth(i)

            options.push({label: valueStr, value: valueStr})
        }

        return options
    }, [])

    const expirationYearOptions = useMemo(() => {
        const year = (new Date()).getFullYear()

        const options = []
        for(let i = year; i <= year + 50; i++) {
            options.push({label: i, value: i})
        }

        return options
    }, [])

    useEffect(() => {
        if(!isFlexLoaded) {
            return
        }

        // credit cards disabled
        if(config.payments.creditCardPaymentUnavailable) return

        if(!window?.Flex || !checkoutInfo.creditCardKey) return

        const flex = new window.Flex(checkoutInfo.creditCardKey)
        const microform = flex.microform({
            styles: flexFormStyles,
        })

        setMicroform(microform)
    }, [
        isFlexLoaded,
        checkoutInfo.creditCardClientLibrary,
        checkoutInfo.creditCardKey,
    ])

    useScript(checkoutInfo.creditCardClientLibrary)

    // preventDefault because otherwise clicking the button will submit the form
    const fillUsingShippingAddress = preventDefault(() => {
        dispatch(fillBillingAddressFromShippingAddress())
    })

    if(checkoutStore.creditCardKey.doing) {
        return <Loading />
    }

    // if we have credit card key errors, then we don't want to display the form
    if(checkoutStore.creditCardKey.errors.length) {
        return <Messages error={errors} />
    }

    if(completeFailureErrors.length) {
        return <Messages error={completeFailureErrors} />
    }

    // credit cards disabled
    if(config.payments.creditCardPaymentUnavailable) {
        return (
            <Messages notice={[<span>Credit card payments are currently unavailable. You may still use PayPal, Affirm, or call us at <a href={`tel:${config.contact.phoneFull}`}>{config.contact.phoneHuman}</a> (credit card orders can be processed over the phone). We're sorry, this is a temporary issue and should be resolved soon.</span>]} />
        )
    }

    return (
        <>
            <Messages error={[...errors, ...cybersourceErrors]} />

            <h4>Credit Card Information:</h4>

            <form onSubmit={handleSubmit}>
                <div className='field-sets'>
                    <div className='field-set-item'>
                        <FormText
                            idPrefix={idPrefix}
                            name='creditCardName'
                            title='Name on Card'
                            value={checkoutInfo.creditCardName}
                            onChange={handleParam('creditCardName')}
                            errors={get('creditCardName', [], paramErrors)}
                            autoComplete='cc-name'
                        />
                        <FormCybersourceField
                            idPrefix={idPrefix}
                            title='Card Number'
                            microform={microform}
                            microformFieldName='number'
                            errors={cardNumberParamErrors}
                            required
                            onCreateFieldError={handleCreateCybersourceFieldError}
                        />
                        <p className='hack-label'>Expiration Month / Year / Security Code</p>
                        <div className='expiration-security'>
                            <FormSelect
                                idPrefix={idPrefix}
                                title='Expiration Month'
                                name='expirationMonth'
                                value={checkoutInfo.creditCardExpirationMonth}
                                placeholder='Month'
                                options={expirationMonthOptions}
                                onChange={handleParam('creditCardExpirationMonth')}
                                errors={get('creditCardExpirationMonth', [], paramErrors)}
                                required
                                autoComplete='cc-exp-month'
                            />
                            <FormSelect
                                idPrefix={idPrefix}
                                title='Month'
                                name='expirationYear'
                                value={checkoutInfo.creditCardExpirationYear}
                                placeholder='Year'
                                options={expirationYearOptions}
                                onChange={handleParam('creditCardExpirationYear')}
                                errors={get('creditCardExpirationYear', [], paramErrors)}
                                required
                                autoComplete='cc-exp-year'
                            />
                            <FormCybersourceField
                                idPrefix={idPrefix}
                                title='Security Code'
                                microform={microform}
                                microformFieldName='securityCode'
                                errors={securityCodeParamErrors}
                                required
                                onCreateFieldError={handleCreateCybersourceFieldError}
                            />
                        </div>
                    </div>
                    <h4 className='billing-header'>Billing Address:</h4>
                    <div className='field-set-item'>
                        {
                            checkoutStore.info.shippingMethod !== SHIPPING_METHOD_HOME
                                ? null
                                : (
                                    <div className='use-shipping'>
                                        <button className='btn btn--blue--dark btn--slim' onClick={fillUsingShippingAddress}>Use shipping address &raquo;</button>
                                    </div>
                                )
                        }

                        <FormText
                            idPrefix={idPrefix}
                            name='address1'
                            title='Address Line 1'
                            value={checkoutInfo.billingAddress1}
                            onChange={handleParam('billingAddress1')}
                            errors={get('billingAddress1', [], paramErrors)}
                            required
                            autoComplete='address-line1'
                        />

                        <FormText
                            idPrefix={idPrefix}
                            name='address2'
                            title='Address Line 2'
                            value={checkoutInfo.billingAddress2}
                            onChange={handleParam('billingAddress2')}
                            errors={get('billingAddress2', [], paramErrors)}
                            autoComplete='address-line2'
                        />
                        <FormText
                            idPrefix={idPrefix}
                            name='city'
                            title='City'
                            value={checkoutInfo.billingCity}
                            onChange={handleParam('billingCity')}
                            errors={get('billingCity', [], paramErrors)}
                            required
                            autoComplete='address-level2'
                        />
                        <div className='state-zip'>
                            <FormUSStateSelect
                                name='state'
                                title='State'
                                value={checkoutInfo.billingState}
                                onChange={handleParam('billingState')}
                                errors={get('billingState', [], paramErrors)}
                                required
                                autoComplete='address-level1'
                            />
                            <FormText
                                idPrefix={idPrefix}
                                name='zip'
                                title='Zip'
                                value={checkoutInfo.billingZip}
                                onChange={handleParam('billingZip')}
                                errors={get('billingZip', [], paramErrors)}
                                required
                                autoComplete='postal-code'
                            />
                        </div>
                    </div>
                </div>

                <CommentField disabled={disabled} />

                <NextStepButton disabled={disabled}>Next: Review Order</NextStepButton>
            </form>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .field-sets {
                        @media(--xl) {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            grid-gap: 30px;
                        }
                    }

                    .field-set-item {
                        max-width: 350px;
                        margin: 0 auto;

                        @media(--xl) {
                            margin: 0;
                            max-width: none;
                        }
                    }

                    .billing-header {
                        @media(--xl) {
                            display: none;
                        }
                    }

                    .state-zip {
                        display: grid;
                        grid-template-columns: 2fr 1fr;
                        grid-gap: 10px;
                    }

                    .expiration-security {
                        display: grid;
                        grid-template-columns: 0.9fr 1fr 0.5fr;
                        grid-gap: 10px;

                        :global(label) {
                            display: none;
                        }
                    }

                    .hack-label {
                        margin-bottom: 0;
                    }

                    .use-shipping {
                        margin-top: 20px;
                        margin-bottom: 10px;
                    }
                `}
            </style>
        </>
    )
}

const GiftCardForm = ({
    errors = [],
    paramErrors = {},
    disabled = false,
}) => {
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const idPrefix = 'checkout-gift-card'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    const handleSubmit = preventDefault(() => {})

    return (
        <>
            <div className='container'>
                <Messages error={errors} />

                <form onSubmit={handleSubmit}>
                    <div className='content'>
                        <h4>Gift Card:</h4>

                        <div className='the-fields'>
                            <FormText
                                idPrefix={idPrefix}
                                name='giftCardNumber'
                                title='Gift Card Number'
                                value={checkoutInfo.giftCardNumber}
                                onChange={handleParam('giftCardNumber')}
                                errors={get('giftCardNumber', [], paramErrors)}
                            />

                            <FormPrice
                                idPrefix={idPrefix}
                                name='giftCardAmount'
                                title='Gift Card Amount'
                                value={checkoutInfo.giftCardAmount}
                                onChange={handleParam('giftCardAmount')}
                                errors={get('giftCardAmount', [], paramErrors)}
                            />
                        </div>
                    </div>
                </form>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    h4 {
                        @media(--md) {
                            margin: 27px 0 0 0; /* align the h4 with the fields. could align-items: center on the content, but then it looks weird if there are errors */
                        }
                    }

                    .container {
                        margin-bottom: 15px;
                        padding-bottom: 10px;
                        border-bottom: 1px solid $c--blue--light-gray;
                    }

                    .content {
                        @media(--md) {
                            display: grid;
                            grid-template-columns: 0.5fr 1.5fr;
                            grid-gap: 15px;
                            align-items: start;
                        }
                    }

                    .the-fields {
                        max-width: 350px;
                        margin: 0 auto;

                        @media(--md) {
                            display: grid;
                            grid-template-columns: 1fr 0.5fr;
                            grid-gap: 15px;
                            align-items: start;
                            margin: 0;
                            max-width: none;
                        }
                    }
                `}
            </style>
        </>
    )
}

const CommentField = ({disabled = false}) => {
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const idPrefix = 'checkout-comment'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    return (
        <>
            <div className='container'>
                <div className='the-fields'>
                    <FormTextArea
                        idPrefix={idPrefix}
                        title='Order Comments (optional):'
                        name='comments'
                        description='Please place any internal reference numbers, additional instructions, requests, and/or questions in the comments box'
                        value={checkoutInfo.comments}
                        onChange={handleParam('comments')}
                    />
                </div>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        margin-top: 15px;
                        padding-top: 10px;
                        border-top: 1px solid $c--blue--light-gray;
                    }

                    .container :global(label) {
                        display: block;
                        font-weight: $weight--bold;
                        margin: 10px 0 15px;
                    }

                    .container :global(.description) {
                        font-size: $size--small;
                    }

                    .container :global(textarea) {
                        min-height: 100px;
                    }

                    .the-fields {
                        max-width: 350px;
                        margin: 0 auto;

                        @media(--lg) {
                            margin: 0;
                            max-width: 450px;
                        }
                    }
                `}
            </style>
        </>
    )
}

const PaymentMethodForm = ({disabled = false}) => {
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const config = useSelector(state => state.config)
    const checkoutInfo = checkoutStore.info

    const {totalToCharge} = getOrderTotals(checkoutStore)

    const idPrefix = 'checkout-payment-method'

    const handlePaymentMethodChange = e => (e && e.target) ? dispatch(setPaymentMethod(e.target.value)) : dispatch(setPaymentMethod(e))

    const options = [
        {label: 'Credit Card', value: PAYMENT_METHOD_CREDIT_CARD},
        totalToCharge >= config.affirm.orderMinimumDollars ? {label: 'Affirm (Buy now, pay over time)', value: PAYMENT_METHOD_AFFIRM} : undefined,
        {label: 'PayPal', value: PAYMENT_METHOD_PAYPAL},
        {label: 'Bill your B&R Autowrecking Account', value: PAYMENT_METHOD_BANDR_ACCOUNT},
    ].filter(Boolean)

    return (
        <>
            <div className='container'>
                <h4>Select Payment Method:</h4>

                <form>
                    <FormCheckboxList
                        kind='radio'
                        idPrefix={idPrefix}
                        name='payment-method'
                        onChange={handlePaymentMethodChange}
                        selectedValue={checkoutInfo.paymentMethod}
                        options={options}
                        disabled={disabled}
                    />
                </form>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        margin-bottom: 15px;
                        border-bottom: 1px solid $c--blue--light-gray;
                    }

                    h4 {
                        margin-bottom: 12px;
                    }

                    form :global(label) {
                        font-style: italic;
                        margin-left: 4px;
                    }

                    form :global(.field-checkbox-item) {
                        margin-bottom: 12px;
                    }
                `}
            </style>
        </>
    )
}

const PaypalPayment = ({onSubmit, disabled, errors = []}) => {
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()

    const handleSubmit = preventDefault(() => {
        dispatch(payWithPaypal(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    })

    return (
        <>
            <Messages error={errors} />

            <form onSubmit={handleSubmit}>
                <p className={bigBlueStyle.className}>After completing your order, you will be directed to paypal.com to complete payment. You will then be redirected back to autowrecking.com.</p>

                <CommentField disabled={disabled} />

                <NextStepButton disabled={disabled}>Next: Review Order</NextStepButton>
            </form>

            {bigBlueStyle.styles}
            <style jsx>
                {`
                    p {
                        margin: 30px 0;
                    }
                `}
            </style>
        </>
    )
}

const AffirmPayment = ({onSubmit, disabled, paramErrors, errors = []}) => {
    const tokenCache = useTokenCache()
    const dispatch = useDispatch()
    const checkoutStore = useSelector(state => state.checkout)
    const checkoutInfo = checkoutStore.info

    const idPrefix = 'checkout-payment-affirm'

    const handleParam = fieldName => e => (e && e.target) ? dispatch(setCheckoutField(fieldName, e.target.value)) : dispatch(setCheckoutField(fieldName, e))

    // preventDefault because otherwise clicking the button will submit the form
    const fillUsingShippingAddress = preventDefault(() => {
        dispatch(fillBillingAddressFromShippingAddress())
    })

    const handleSubmit = preventDefault(() => {
        dispatch(payWithAffirm(tokenCache))
            .then(success => {
                if(success) {
                    onSubmit()
                }
            })
    })

    return (
        <>
            <Messages error={errors} />

            <form onSubmit={handleSubmit}>
                <div className='affirm'>
                    <div className='image'>
                        <Image width='100' height='56' src='/images/logos/affirm.svg' alt='Affirm logo' />
                    </div>
                    <div className='lead'>Buy now, pay over time</div>
                    <div className='message'>Make monthly payments with no hidden fees.</div>
                    <div className='learn-more'>
                        <a href='https://www.affirm.com/how-it-works' className='btn btn--blue--dark' target='_blank' rel='noopener noreferrer'>Learn more</a>
                    </div>
                </div>

                <p className={bigBlueStyle.className}>After completing your order, you will be directed to affirm.com to complete payment. You will then be redirected back to autowrecking.com.</p>

                <h4>Billing Address:</h4>

                <div className='field-set-item'>
                    {
                        checkoutStore.info.shippingMethod !== SHIPPING_METHOD_HOME
                            ? null
                            : (
                                <div className='use-shipping'>
                                    <button className='btn btn--blue--dark btn--slim' onClick={fillUsingShippingAddress}>Use shipping address &raquo;</button>
                                </div>
                            )
                    }

                    <FormText
                        idPrefix={idPrefix}
                        name='name'
                        title='Name'
                        value={checkoutInfo.billingName}
                        onChange={handleParam('billingName')}
                        errors={get('billingName', [], paramErrors)}
                        required
                        autoComplete='name'
                    />

                    <FormText
                        idPrefix={idPrefix}
                        name='address1'
                        title='Address Line 1'
                        value={checkoutInfo.billingAddress1}
                        onChange={handleParam('billingAddress1')}
                        errors={get('billingAddress1', [], paramErrors)}
                        required
                        autoComplete='address-line1'
                    />

                    <FormText
                        idPrefix={idPrefix}
                        name='address2'
                        title='Address Line 2'
                        value={checkoutInfo.billingAddress2}
                        onChange={handleParam('billingAddress2')}
                        errors={get('billingAddress2', [], paramErrors)}
                        autoComplete='address-line2'
                    />
                    <FormText
                        idPrefix={idPrefix}
                        name='city'
                        title='City'
                        value={checkoutInfo.billingCity}
                        onChange={handleParam('billingCity')}
                        errors={get('billingCity', [], paramErrors)}
                        required
                        autoComplete='address-level2'
                    />
                    <div className='state-zip'>
                        <FormUSStateSelect
                            name='state'
                            title='State'
                            value={checkoutInfo.billingState}
                            onChange={handleParam('billingState')}
                            errors={get('billingState', [], paramErrors)}
                            required
                            autoComplete='address-level1'
                        />
                        <FormText
                            idPrefix={idPrefix}
                            name='zip'
                            title='Zip'
                            value={checkoutInfo.billingZip}
                            onChange={handleParam('billingZip')}
                            errors={get('billingZip', [], paramErrors)}
                            required
                            autoComplete='postal-code'
                        />
                    </div>
                    <FormEmail
                        idPrefix={idPrefix}
                        name='email'
                        title='Email'
                        value={checkoutInfo.billingEmail}
                        onChange={handleParam('billingEmail')}
                        errors={get('billingEmail', [], paramErrors)}
                        required
                        autoComplete='email'
                    />
                    <FormPhone
                        idPrefix={idPrefix}
                        name='phone'
                        title='Phone'
                        value={checkoutInfo.billingPhone}
                        onChange={handleParam('billingPhone')}
                        errors={get('billingPhone', [], paramErrors)}
                        required
                        autoComplete='tel'
                    />
                </div>

                <CommentField disabled={disabled} />

                <NextStepButton disabled={disabled}>Next: Review Order</NextStepButton>
            </form>

            {bigBlueStyle.styles}
            <style jsx>
                {`
                    @import '@css/variables.css';

                    p {
                        margin: 30px 0;
                    }

                    .field-sets {
                        @media(--xl) {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            grid-gap: 30px;
                        }
                    }

                    .field-set-item {
                        max-width: 350px;
                        margin: 0 auto;

                        @media(--xl) {
                            margin: 0;
                            max-width: none;
                        }
                    }

                    .state-zip {
                        display: grid;
                        grid-template-columns: 2fr 1fr;
                        grid-gap: 10px;
                    }

                    .use-shipping {
                        margin-top: 20px;
                        margin-bottom: 10px;
                    }

                    .affirm {
                        max-width: 200px;
                        text-align: center;
                        margin: 0 auto;

                        .image {
                            display: inline-block;
                            text-align: center;
                            margin-bottom: 5px;
                        }

                        .lead {
                            display: inline-block;
                            text-align: center;
                            font-weight: $weight--bold;
                            font-size: $size--larger;
                            margin-bottom: 12px;
                            line-height: $line-height--short;
                        }

                        .message {
                            display: inline-block;
                            text-align: center;
                            font-size: $size--medium;
                            margin-bottom: 20px;
                        }
                    }
                `}
            </style>
        </>
    )
}

export default function StepBilling({showSummary, onSubmit, disabled = false}) {
    const checkoutStore = useSelector(state => state.checkout)

    const paymentMethod = checkoutStore.info.paymentMethod

    const renderPaymentMethodForm = paymentMethod => {
        if(paymentMethod === PAYMENT_METHOD_PAYPAL) {
            return <PaypalPayment onSubmit={onSubmit} disabled={disabled} errors={checkoutStore.payWithPaypal.errors} />
        }
        else if(paymentMethod === PAYMENT_METHOD_AFFIRM) {
            return <AffirmPayment onSubmit={onSubmit} disabled={disabled} errors={checkoutStore.payWithAffirm.errors} paramErrors={checkoutStore.payWithAffirm.paramErrors} />
        }
        else if(paymentMethod === PAYMENT_METHOD_BANDR_ACCOUNT) {
            return <BandrAccountPaymentForm onSubmit={onSubmit} disabled={disabled} errors={checkoutStore.payWithBandrAccount.errors} paramErrors={checkoutStore.payWithBandrAccount.paramErrors} />
        }
        else if(paymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
            return <CreditCardPaymentForm onSubmit={onSubmit} disabled={disabled} errors={[...checkoutStore.payWithCreditCard.errors, ...checkoutStore.creditCardKey.errors]} paramErrors={checkoutStore.payWithCreditCard.paramErrors} />
        }
        else {
            return null
        }
    }

    if(showSummary) {
        const paymentTokenPayload = checkoutStore.info.paymentTokenPayload || {}

        if(paymentMethod === PAYMENT_METHOD_PAYPAL) {
            return (
                <StepSummaryItem title='Payment Method'>PayPal</StepSummaryItem>
            )
        }
        else if(paymentMethod === PAYMENT_METHOD_AFFIRM) {
            return (
                <StepSummaryItem title='Payment Method'>Affirm</StepSummaryItem>
            )
        }
        else if(paymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
            return (
                <>
                    <StepSummaryItem title='Payment Method'>Credit Card</StepSummaryItem>
                    <StepSummaryItem title='Card Number'><PlaceholderDots /> {paymentTokenPayload.creditCardLastFour}</StepSummaryItem>
                    <StepSummaryItem title='Expires'>{paymentTokenPayload.creditCardExpirationMonth} / {paymentTokenPayload.creditCardExpirationYear}</StepSummaryItem>
                </>
            )
        }
        else if(paymentMethod === PAYMENT_METHOD_BANDR_ACCOUNT) {
            return (
                <>
                    <StepSummaryItem title='Payment Method'>B&R Account</StepSummaryItem>
                    <StepSummaryItem title='Account Number'><PlaceholderDots /> {getEnding(4, paymentTokenPayload.bandrAccountNumber)}</StepSummaryItem>
                    <StepSummaryItem title='P.O. Number'><PlaceholderDots /> {getEnding(4, paymentTokenPayload.bandrAccountPoNumber)}</StepSummaryItem>
                </>
            )
        }
        else {
            return null
        }
    }
    else {
        return (
            <div className='step-billing'>
                <Messages error={checkoutStore.generalPayment.errors} />

                <GiftCardForm disabled={disabled} errors={checkoutStore.payWithGiftCard.errors} paramErrors={checkoutStore.payWithGiftCard.paramErrors} />

                <PaymentMethodForm disabled={disabled} />

                {renderPaymentMethodForm(paymentMethod)}
            </div>
        )
    }
}
